import { differenceInCalendarDays } from 'date-fns';

import { useCurrentCompany, useCurrentUser } from '@bonusly/who-am-i';

// Days after `trial_end_date` that the trial "what's next?" surfaces keep
// nudging a lapsed account to pick a plan. Matches the backend's
// `trial_ended_recently?` (`trial_end > 14.days.ago`, exclusive), so the banner
// and card never link to a page that 404s for an out-of-window company.
// `differenceInCalendarDays` floors to whole days, so `< 14` keeps the client
// window inside the server's to avoid a blank page at the boundary.
export const POST_EXPIRY_WINDOW_DAYS = 14;

export interface TrialStatus {
	// Days until `trial_end_date` (negative once it has passed); `null` when the
	// company has no `trial_end_date`. `daysSinceEnd` is the same value negated.
	daysLeft: number | null;
	daysSinceEnd: number | null;
	isInTrial: boolean;
	// Trial lapsed without converting and still inside the post-expiry window.
	// Never true for paying companies (active / non_renewing) — they converted.
	isPostExpiry: boolean;
	// Whether the trial "what's next?" surfaces are relevant to this company at
	// all: in trial or recently lapsed, and not a Bonusly-internal
	// superadmin-initialized company. Role is separate (`canSeeWhatsNext`).
	isInScope: boolean;
	// The `admin/trial/whats_next` endpoint authorizes finance admins; global
	// admins are a superset of finance, so both qualify.
	canSeeWhatsNext: boolean;
	isSuperadminInitialized: boolean;
}

/**
 * Single source of truth for trial timing and eligibility, shared by the admin
 * trial banner, the user-navbar trial card, and the login interstitial gate.
 * These each used to re-derive the same date math and drifted (the 30-vs-14-day
 * window, the active-vs-paying exclusion). Computed entirely during render from
 * already-loaded who-am-i data — no effects.
 *
 * The endpoint stays authoritative for the auto-present decision
 * (`shouldAutoPresent`) and for finance-permission gating; this hook is the
 * cheap client-side pre-filter and drives the copy the surfaces render
 * themselves.
 */
export const useTrialStatus = (): TrialStatus => {
	const user = useCurrentUser();
	const company = useCurrentCompany();

	const trialEndDate = company?.trial_end_date;
	const daysLeft = trialEndDate ? differenceInCalendarDays(new Date(trialEndDate), new Date()) : null;
	const daysSinceEnd = daysLeft === null ? null : -daysLeft;

	// Require a real `trial_end_date`: an `in_trial` company without one has no
	// meaningful "days left", so we don't render timing copy (e.g. "0 days left")
	// or treat it as in scope, matching the pre-refactor trial card.
	const isInTrial = company?.status === 'in_trial' && daysLeft !== null && daysLeft >= 0;

	// Paying companies (active or non_renewing) have converted from trial, so
	// they are never "post-expiry" even if their trial_end_date is recent. This
	// mirrors the backend's `subscription.paying?` exclusion.
	const isPaying = company?.status === 'active' || company?.status === 'non_renewing';
	const isPostExpiry = !isInTrial && !isPaying && daysSinceEnd !== null && daysSinceEnd >= 0 && daysSinceEnd < POST_EXPIRY_WINDOW_DAYS;

	const isSuperadminInitialized = !!company?.is_superadmin_initialized;
	const canSeeWhatsNext = !!user?.finance_admin || !!user?.global_admin;

	return {
		daysLeft,
		daysSinceEnd,
		isInTrial,
		isPostExpiry,
		isInScope: (isInTrial || isPostExpiry) && !isSuperadminInitialized,
		canSeeWhatsNext,
		isSuperadminInitialized,
	};
};
